/**
 * OrgOverviewPage Component
 *
 * Executive landing page for Radar de Desempenho.
 * Shows organization-wide metrics, top performers, and areas needing attention.
 */
import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Users, TrendingUp, Target, ArrowRight, AlertTriangle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrgOverview } from '@/hooks/queries/useRadarQueries'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import { RadarCard } from './RadarCard'
import { EngagementIndicator } from './EngagementIndicator'
import { PerformanceScoreBadge } from './PerformanceScoreBadge'
import { PerformanceDistributionChart } from './PerformanceDistributionChart'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export function OrgOverviewPage() {
  const navigate = useNavigate()
  // Hidden: favorites feature temporarily disabled
  // const { getCurrentPeriod, favorites, toggleFavorite } = useRadarUIStore()
  const { getCurrentPeriod } = useRadarUIStore()
  const period = getCurrentPeriod()

  const { data, isLoading, isError, error } = useOrgOverview(period)

  // Loading state
  if (isLoading) {
    return <OrgOverviewSkeleton />
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Erro ao carregar dados</h2>
        <p className="text-brand-gray mb-4">
          {error instanceof Error ? error.message : 'Ocorreu um erro ao carregar os dados do radar.'}
        </p>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Validate required data structure
  if (!data.organization || !data.engagement || !data.okrs || !data.metadata) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Dados incompletos</h2>
        <p className="text-brand-gray mb-4">
          A API retornou dados incompletos. Verifique se o backend está funcionando corretamente.
        </p>
      </div>
    )
  }

  const lastUpdated = formatDistanceToNow(new Date(data.metadata.generatedAt), {
    addSuffix: true,
    locale: ptBR,
  })

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-2xl font-semibold text-white">Visão Geral da Organização</h2>
        <p className="text-brand-gray">Visão executiva da performance organizacional</p>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Employees */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-brand-primary/10">
                <Users className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <p className="text-sm text-brand-gray">Total de Funcionários</p>
                <p className="text-3xl font-bold text-white">{data.organization.totalEmployees}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Performance */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-brand-gray">Média de Performance</p>
                <div className="flex items-center gap-2">
                  <PerformanceScoreBadge score={data.organization.avgPerformanceScore} size="lg" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hidden: Engagement card - feature temporarily disabled
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-brand-primary/10">
                <Target className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <p className="text-sm text-brand-gray">Engajamento</p>
                <div className="flex items-center gap-2">
                  <EngagementIndicator signal={data.engagement.overallHealth} showLabel size="lg" />
                  <span className="text-sm text-brand-gray">
                    {Math.round(data.engagement.oneOnOneCoverage)}% cobertura 1-on-1
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        */}
      </div>

      {/* Distribution and OKRs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <PerformanceDistributionChart distribution={data.organization.distribution} />
          </CardContent>
        </Card>

        {/* OKR Completion */}
        <Card>
          <CardHeader>
            <CardTitle>Conclusão de OKRs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Taxa de Conclusão</span>
                <span className="text-2xl font-bold text-white">
                  {Math.round(data.okrs.companyCompletionRate)}%
                </span>
              </div>
              <div className="h-3 bg-brand-dark-light rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full transition-all duration-500"
                  style={{ width: `${data.okrs.companyCompletionRate}%` }}
                />
              </div>
              {data.okrs.atRiskCount > 0 && (
                <div className="flex items-center gap-2 text-yellow-500 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {data.okrs.atRiskCount} OKRs em risco
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Top Performers</CardTitle>
            <CardDescription>Funcionários com melhor desempenho no período</CardDescription>
          </div>
          <button
            onClick={() => {
              // TODO: Navigate to full list
            }}
            className="flex items-center gap-1 text-sm text-brand-primary hover:underline"
          >
            Ver todos <ArrowRight className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.topPerformers.slice(0, 6).map((performer) => (
              <RadarCard
                key={performer.employee.employeeId}
                employee={performer.employee}
                performanceScore={performer.performanceScore}
                teamSize={performer.teamSize}
                engagementSignal={performer.engagementSignal}
                variant="card"
                onClick={() => navigate({
                  to: '/performance/radar/$employeeId',
                  params: { employeeId: performer.employee.employeeId }
                })}
                /* Hidden: favorites feature temporarily disabled
                isFavorite={favorites.includes(performer.employee.employeeId)}
                onFavorite={() => toggleFavorite(performer.employee.employeeId)}
                */
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* At-Risk Areas */}
      {data.atRiskAreas.length > 0 && (
        <Card className="border-yellow-500/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Atenção Necessária
              </CardTitle>
              <CardDescription>Funcionários ou áreas que precisam de acompanhamento</CardDescription>
            </div>
            <button
              onClick={() => {
                // TODO: Navigate to full list
              }}
              className="flex items-center gap-1 text-sm text-brand-primary hover:underline"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.atRiskAreas.slice(0, 6).map((area) => (
                <RadarCard
                  key={area.employee.employeeId}
                  employee={area.employee}
                  performanceScore={area.performanceScore}
                  teamSize={area.teamSize}
                  engagementSignal={area.engagementSignal}
                  variant="card"
                  onClick={() => navigate({
                    to: '/performance/radar/$employeeId',
                    params: { employeeId: area.employee.employeeId }
                  })}
                  /* Hidden: favorites feature temporarily disabled
                  isFavorite={favorites.includes(area.employee.employeeId)}
                  onFavorite={() => toggleFavorite(area.employee.employeeId)}
                  */
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last updated */}
      <div className="text-center text-sm text-brand-gray">
        Última atualização: {lastUpdated}
      </div>
    </div>
  )
}

/**
 * Skeleton loading state
 */
function OrgOverviewSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-64 bg-brand-gray/20 mb-2" />
        <Skeleton className="h-5 w-96 bg-brand-gray/20" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Skeleton className="w-12 h-12 rounded-lg bg-brand-gray/20" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24 bg-brand-gray/20" />
                  <Skeleton className="h-8 w-16 bg-brand-gray/20" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48 bg-brand-gray/20" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="space-y-1">
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-32 bg-brand-gray/20" />
                      <Skeleton className="h-4 w-8 bg-brand-gray/20" />
                    </div>
                    <Skeleton className="h-2 w-full bg-brand-gray/20" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-36 bg-brand-gray/20" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 bg-brand-gray/20 rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
